import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Card,
  Chip,
  LinearProgress,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { ArrowBackRounded } from "@mui/icons-material";
import { signOut } from "firebase/auth";
import { doc, getDoc, setDoc } from "firebase/firestore";
import { useNavigate } from "react-router-dom";
import MainLayout from "../../../layouts/main_layout/main_layout";
import { useFeedback } from "../../../providers/feedback/feedback";
import { firestore } from "../../..";
import { getYoucatQuizAuth } from "./quiz_auth";
import {
  buildQuizSubmissionDocId,
  evaluateQuizSubmission,
  formatDurationLabel,
  formatQuizDateTime,
  YOUCAT_QUIZ_SETTINGS_COLLECTION,
  YOUCAT_QUIZ_SETTINGS_DOC_ID,
  YOUCAT_QUIZ_SUBMISSIONS_COLLECTION,
  YOUCAT_QUIZ_USERS_COLLECTION,
  YoucatQuizSettings,
  YoucatQuizSubmission,
  YoucatQuizUserProfile,
} from "./quiz_shared";

function getRemainingSeconds(expiresAt?: string) {
  if (!expiresAt) {
    return 0;
  }

  const ms = new Date(expiresAt).getTime() - Date.now();
  return Math.max(0, Math.floor(ms / 1000));
}

export default function YoucatQuizPortalPage() {
  const navigate = useNavigate();
  const quizAuth = useMemo(() => getYoucatQuizAuth(), []);
  const { showSnackbar } = useFeedback();
  const [settings, setSettings] = useState<YoucatQuizSettings | null>(null);
  const [userProfile, setUserProfile] = useState<YoucatQuizUserProfile | null>(null);
  const [submission, setSubmission] = useState<YoucatQuizSubmission | null>(null);
  const [answers, setAnswers] = useState<Record<string, string>>({});
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [timeLeftSeconds, setTimeLeftSeconds] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const warningHistoryRef = useRef<Record<string, boolean>>({});
  const autoSubmitStartedRef = useRef(false);

  const questionCount = settings?.questions.length || 0;
  const currentQuestion = settings?.questions[currentQuestionIndex] || null;

  const scheduleState = useMemo(() => {
    if (!settings?.startAt || !settings?.endAt) {
      return "invalid";
    }

    const now = Date.now();
    const start = new Date(settings.startAt).getTime();
    const end = new Date(settings.endAt).getTime();

    if (Number.isNaN(start) || Number.isNaN(end)) {
      return "invalid";
    }

    if (now < start) {
      return "before_start";
    }

    if (now > end) {
      return "ended";
    }

    return "active";
  }, [settings]);

  async function persistSubmission(nextSubmission: Partial<YoucatQuizSubmission>) {
    if (!settings?.quizId || !quizAuth.currentUser?.uid) {
      return;
    }

    const docId = buildQuizSubmissionDocId(settings.quizId, quizAuth.currentUser.uid);
    await setDoc(
      doc(firestore, YOUCAT_QUIZ_SUBMISSIONS_COLLECTION, docId),
      nextSubmission,
      { merge: true },
    );
  }

  useEffect(() => {
    let isMounted = true;

    async function loadPortalData() {
      if (!quizAuth.currentUser?.uid) {
        setIsLoading(false);
        return;
      }

      try {
        const settingsSnapshot = await getDoc(
          doc(firestore, YOUCAT_QUIZ_SETTINGS_COLLECTION, YOUCAT_QUIZ_SETTINGS_DOC_ID),
        );

        const nextSettings = settingsSnapshot.exists()
          ? ((settingsSnapshot.data() as YoucatQuizSettings) || null)
          : null;

        const profileSnapshot = await getDoc(
          doc(firestore, YOUCAT_QUIZ_USERS_COLLECTION, quizAuth.currentUser.uid),
        );
        const nextProfile = profileSnapshot.exists()
          ? ((profileSnapshot.data() as YoucatQuizUserProfile) || null)
          : null;

        let nextSubmission: YoucatQuizSubmission | null = null;

        if (nextSettings?.quizId) {
          const submissionSnapshot = await getDoc(
            doc(
              firestore,
              YOUCAT_QUIZ_SUBMISSIONS_COLLECTION,
              buildQuizSubmissionDocId(nextSettings.quizId, quizAuth.currentUser.uid),
            ),
          );

          nextSubmission = submissionSnapshot.exists()
            ? ({
                docId: submissionSnapshot.id,
                ...(submissionSnapshot.data() as Omit<YoucatQuizSubmission, "docId">),
              } as YoucatQuizSubmission)
            : null;
        }

        if (!isMounted) {
          return;
        }

        setSettings(nextSettings);
        setUserProfile(nextProfile);
        setSubmission(nextSubmission);
        setAnswers(nextSubmission?.answers || {});
        setCurrentQuestionIndex(nextSubmission?.currentQuestionIndex || 0);
        setTimeLeftSeconds(getRemainingSeconds(nextSubmission?.expiresAt));
      } catch (error) {
        console.error("Failed to load YouCat quiz portal", error);
        if (isMounted) {
          showSnackbar("Failed to load quiz data");
        }
      } finally {
        if (isMounted) {
          setIsLoading(false);
        }
      }
    }

    loadPortalData();

    return () => {
      isMounted = false;
    };
  }, [quizAuth, showSnackbar]);

  async function handleStartQuiz() {
    if (!settings || !quizAuth.currentUser || !userProfile) {
      return;
    }

    const now = Date.now();
    const windowEnd = new Date(settings.endAt).getTime();
    const configuredDurationMs = settings.totalDurationSeconds * 1000;
    const expiresAt = new Date(
      Math.min(now + configuredDurationMs, windowEnd),
    ).toISOString();

    const nextSubmission: YoucatQuizSubmission = {
      docId: buildQuizSubmissionDocId(settings.quizId, quizAuth.currentUser.uid),
      quizId: settings.quizId,
      quizTitle: settings.title,
      userUid: quizAuth.currentUser.uid,
      userProfile,
      status: "in_progress",
      approvalStatus: "pending",
      startedAt: new Date(now).toISOString(),
      expiresAt,
      currentQuestionIndex: 0,
      answers: {},
      questionSnapshots: settings.questions,
      evaluationByQuestion: {},
      autoScore: 0,
      maxScore: settings.questions.reduce((total, question) => total + question.points, 0),
    };

    try {
      setIsSaving(true);
      await persistSubmission(nextSubmission);
      setSubmission(nextSubmission);
      setAnswers({});
      setCurrentQuestionIndex(0);
      setTimeLeftSeconds(getRemainingSeconds(expiresAt));
      warningHistoryRef.current = {};
      showSnackbar("Quiz started. Use Save & Next after each question.");
    } catch (error) {
      console.error("Failed to start YouCat quiz", error);
      showSnackbar("Failed to start the quiz");
    } finally {
      setIsSaving(false);
    }
  }

  async function handleSaveAndNext() {
    if (!submission) {
      return;
    }

    const nextQuestionIndex = Math.min(currentQuestionIndex + 1, Math.max(questionCount - 1, 0));

    try {
      setIsSaving(true);
      await persistSubmission({
        answers,
        currentQuestionIndex: nextQuestionIndex,
        updatedAt: new Date().toISOString(),
      } as Partial<YoucatQuizSubmission>);
      setCurrentQuestionIndex(nextQuestionIndex);
      setSubmission((prev) =>
        prev
          ? {
              ...prev,
              answers,
              currentQuestionIndex: nextQuestionIndex,
            }
          : prev,
      );
      showSnackbar(
        currentQuestionIndex + 1 >= questionCount
          ? "Answer saved. You can submit when ready."
          : "Answer saved. Moving to the next question.",
      );
    } catch (error) {
      console.error("Failed to save quiz progress", error);
      showSnackbar("Failed to save quiz progress");
    } finally {
      setIsSaving(false);
    }
  }

  async function handleSubmitQuiz(forceTimeout = false) {
    if (!submission || !settings || autoSubmitStartedRef.current) {
      return;
    }

    autoSubmitStartedRef.current = true;

    try {
      setIsSaving(true);
      const evaluation = evaluateQuizSubmission(
        submission.questionSnapshots,
        answers,
      );
      const submittedAt = new Date().toISOString();

      await persistSubmission({
        answers,
        currentQuestionIndex,
        status: forceTimeout ? "expired" : "submitted",
        approvalStatus: "pending",
        submittedAt,
        evaluationByQuestion: evaluation.evaluationByQuestion,
        autoScore: evaluation.autoScore,
        maxScore: evaluation.maxScore,
      });

      const nextSubmission: YoucatQuizSubmission = {
        ...submission,
        answers,
        currentQuestionIndex,
        status: forceTimeout ? "expired" : "submitted",
        approvalStatus: "pending",
        submittedAt,
        evaluationByQuestion: evaluation.evaluationByQuestion,
        autoScore: evaluation.autoScore,
        maxScore: evaluation.maxScore,
      };

      setSubmission(nextSubmission);
      showSnackbar(
        forceTimeout
          ? "Time is up. Your saved and answered responses were submitted automatically."
          : "Quiz submitted successfully. Final marks will appear after admin approval.",
      );
    } catch (error) {
      console.error("Failed to submit quiz", error);
      showSnackbar("Failed to submit quiz");
      autoSubmitStartedRef.current = false;
    } finally {
      setIsSaving(false);
    }
  }

  useEffect(() => {
    if (!submission?.expiresAt || submission.status !== "in_progress") {
      return;
    }

    setTimeLeftSeconds(getRemainingSeconds(submission.expiresAt));
    const interval = window.setInterval(() => {
      const nextTimeLeft = getRemainingSeconds(submission.expiresAt);
      setTimeLeftSeconds(nextTimeLeft);
    }, 1000);

    return () => window.clearInterval(interval);
  }, [submission?.expiresAt, submission?.status]);

  useEffect(() => {
    if (submission?.status !== "in_progress") {
      return;
    }

    if ([120, 90, 10].includes(timeLeftSeconds) && !warningHistoryRef.current[String(timeLeftSeconds)]) {
      warningHistoryRef.current[String(timeLeftSeconds)] = true;
      showSnackbar(`Only ${formatDurationLabel(timeLeftSeconds)} left`);
    }

    if (timeLeftSeconds <= 10 && timeLeftSeconds > 0 && !warningHistoryRef.current[`sec-${timeLeftSeconds}`]) {
      warningHistoryRef.current[`sec-${timeLeftSeconds}`] = true;
      showSnackbar(`Only ${timeLeftSeconds} second${timeLeftSeconds === 1 ? "" : "s"} left`);
    }

    if (timeLeftSeconds <= 0) {
      handleSubmitQuiz(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [timeLeftSeconds, submission?.status, showSnackbar]);

  useEffect(() => {
    if (submission?.status !== "in_progress") {
      return;
    }

    const handleBeforeUnload = (event: BeforeUnloadEvent) => {
      event.preventDefault();
      event.returnValue = "";
    };

    window.addEventListener("beforeunload", handleBeforeUnload);
    return () => window.removeEventListener("beforeunload", handleBeforeUnload);
  }, [submission?.status]);

  async function handleLogout() {
    await signOut(quizAuth);
    navigate("/youcat/quiz");
  }

  if (isLoading) {
    return (
      <MainLayout>
        <Box my={{ xs: 2, md: 4 }}>
          <Typography>Loading YouCat quiz portal...</Typography>
        </Box>
      </MainLayout>
    );
  }

  const canStartQuiz =
    Boolean(quizAuth.currentUser) &&
    Boolean(userProfile) &&
    settings?.isQuizAvailable !== false &&
    scheduleState === "active" &&
    !submission;

  const showExistingResult = submission && submission.status !== "in_progress";
  const progressPercent = questionCount
    ? Math.min(100, ((currentQuestionIndex + 1) / questionCount) * 100)
    : 0;

  return (
    <MainLayout>
      <Box my={{ xs: 2, md: 4 }}>
        <Stack direction={{ xs: "column", sm: "row" }} gap={2} mb={2}>
          <Button startIcon={<ArrowBackRounded />} onClick={() => navigate("/youcat")}>
            Back to YouCat
          </Button>
          <Button variant="outlined" onClick={handleLogout}>
            Logout
          </Button>
        </Stack>

        <Card
          sx={{
            p: { xs: 2.5, md: 3.5 },
            borderRadius: 4,
            border: "1px solid rgba(217,119,6,0.12)",
            boxShadow: "0 24px 48px rgba(180, 120, 20, 0.08)",
          }}
        >
          <Typography variant="overline" sx={{ color: "#ffbd61", fontWeight: 800 }}>
            YouCat Quiz
          </Typography>
          <Typography variant="h4" fontWeight={900} color="#dbab3b" mt={1}>
            {settings?.title || "YouCat Quiz Portal"}
          </Typography>
          <Typography color="#7c651d" mt={1}>
            {settings?.description || "Quiz details will appear here once the admin publishes them."}
          </Typography>

          {settings?.instructions ? (
            <Alert severity="info" sx={{ mt: 3 }}>
              {settings.instructions}
            </Alert>
          ) : null}

          {!quizAuth.currentUser ? (
            <Alert severity="warning" sx={{ mt: 3 }}>
              No quiz user is signed in right now. Please login again before attending the quiz.
            </Alert>
          ) : null}

          {!userProfile && quizAuth.currentUser ? (
            <Alert severity="warning" sx={{ mt: 3 }}>
              Your quiz profile could not be found. Please sign up again before attempting the quiz.
            </Alert>
          ) : null}

          {settings?.startAt || settings?.endAt ? (
            <Stack spacing={0.5} mt={3}>
              <Typography color="#7c651d">
                Start: {formatQuizDateTime(settings?.startAt)}
              </Typography>
              <Typography color="#7c651d">
                End: {formatQuizDateTime(settings?.endAt)}
              </Typography>
              <Typography color="#7c651d">
                Quiz timer: {formatDurationLabel(settings?.totalDurationSeconds || 0)}
              </Typography>
            </Stack>
          ) : null}

          {settings?.isQuizAvailable === false ? (
            <Alert severity="info" sx={{ mt: 3 }}>
              The quiz is currently unavailable. Your account stays ready for the next release.
            </Alert>
          ) : null}

          {scheduleState === "before_start" ? (
            <Alert severity="info" sx={{ mt: 3 }}>
              The quiz has not started yet. It opens on {formatQuizDateTime(settings?.startAt)}.
            </Alert>
          ) : null}

          {scheduleState === "ended" ? (
            <Alert severity="warning" sx={{ mt: 3 }}>
              The quiz window closed on {formatQuizDateTime(settings?.endAt)}.
            </Alert>
          ) : null}

          {scheduleState === "invalid" ? (
            <Alert severity="warning" sx={{ mt: 3 }}>
              The admin still needs to finish the quiz schedule configuration.
            </Alert>
          ) : null}

          {canStartQuiz ? (
            <Box mt={3}>
              <Button variant="contained" onClick={handleStartQuiz} disabled={isSaving}>
                {isSaving ? "Starting..." : "Start Quiz"}
              </Button>
            </Box>
          ) : null}

          {submission?.status === "in_progress" && currentQuestion ? (
            <Box mt={4}>
              <Stack
                direction={{ xs: "column", md: "row" }}
                justifyContent="space-between"
                alignItems={{ xs: "flex-start", md: "center" }}
                gap={2}
                mb={2}
              >
                <Box>
                  <Typography variant="h6" fontWeight={800}>
                    Question {currentQuestionIndex + 1} of {questionCount}
                  </Typography>
                  <Typography color="#7c651d">
                    {currentQuestion.required ? "Required" : "Optional"} | {currentQuestion.points} point{currentQuestion.points === 1 ? "" : "s"}
                  </Typography>
                </Box>
                <Chip
                  color={timeLeftSeconds <= 90 ? "warning" : "success"}
                  label={`Time Left: ${formatDurationLabel(timeLeftSeconds)}`}
                />
              </Stack>

              <LinearProgress variant="determinate" value={progressPercent} sx={{ mb: 3 }} />

              <Typography variant="h6" fontWeight={700} mb={2}>
                {currentQuestion.question}
              </Typography>

              {currentQuestion.type === "mcq" ? (
                <TextField
                  select
                  fullWidth
                  label="Select your answer"
                  value={answers[currentQuestion.id] || ""}
                  onChange={(event) =>
                    setAnswers((prev) => ({
                      ...prev,
                      [currentQuestion.id]: event.target.value,
                    }))
                  }
                >
                  {currentQuestion.options.map((option) => (
                    <MenuItem key={option} value={option}>
                      {option}
                    </MenuItem>
                  ))}
                </TextField>
              ) : (
                <TextField
                  fullWidth
                  multiline={currentQuestion.type === "long_answer"}
                  minRows={currentQuestion.type === "long_answer" ? 5 : 1}
                  label={
                    currentQuestion.type === "short_answer"
                      ? "Enter one word answer"
                      : "Enter your answer"
                  }
                  value={answers[currentQuestion.id] || ""}
                  onChange={(event) =>
                    setAnswers((prev) => ({
                      ...prev,
                      [currentQuestion.id]: event.target.value,
                    }))
                  }
                />
              )}

              <Stack direction={{ xs: "column", sm: "row" }} gap={2} mt={3}>
                <Button
                  variant="outlined"
                  onClick={() =>
                    setCurrentQuestionIndex((prev) => Math.max(0, prev - 1))
                  }
                  disabled={currentQuestionIndex === 0}
                >
                  Previous
                </Button>
                <Button variant="contained" onClick={handleSaveAndNext} disabled={isSaving}>
                  {isSaving ? "Saving..." : "Save & Next"}
                </Button>
                <Button
                  variant="contained"
                  color="success"
                  onClick={() => handleSubmitQuiz(false)}
                  disabled={isSaving}
                >
                  Submit Quiz
                </Button>
              </Stack>
            </Box>
          ) : null}

          {showExistingResult ? (
            <Alert severity={submission?.approvalStatus === "approved" ? "success" : "info"} sx={{ mt: 3 }}>
              {submission?.approvalStatus === "approved"
                ? `Final marks approved: ${
                    typeof submission.finalScore === "number"
                      ? submission.finalScore
                      : submission?.autoScore || 0
                  }/${submission?.maxScore || 0}`
                : "Your quiz has been submitted. Final marks will be published after admin approval."}
            </Alert>
          ) : null}
        </Card>
      </Box>
    </MainLayout>
  );
}
